import React, { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { useConfig } from '../../contexts/ConfigContext';
import { getSpecializationTerm, isSpecializationDirection, getDirectionTerms } from '../../utils/organizationHelpers';
import { useIframeCommunication } from '../../hooks/useIframeCommunication';
import { BookOpen, ArrowRight, ArrowLeft } from 'lucide-react';
import Footer from '../../components/Footer';
import './DirectionsListPage.css';

const DirectionsListPage = () => {
    useIframeCommunication();
    const navigate = useNavigate();
    const { config } = useConfig();
    const directionTerms = getDirectionTerms(config?.code);

    const handleBack = () => {
        if (window.history.length > 1) {
            navigate(-1);
        } else {
            navigate('/public/groups');
        }
    };

    const { data: directions, isLoading } = useQuery(
        'directionsAll',
        () => axios.get(`${API_BASE_URL}/directions/public`).then(res => res.data),
        {
            retry: 1,
            refetchOnWindowFocus: false
        }
    );

    // Сортируем направления: специализация/авторская программа - первыми
    const sortedDirections = useMemo(() => {
        if (!directions || !config) return directions || [];

        const orgCode = config.code || 'mag';
        const specializationTerm = getSpecializationTerm(orgCode);

        // Разделяем на специализации и остальные
        const specializationDirs = [];
        const otherDirs = [];

        directions.forEach(dir => {
            const displayName = dir.full_name || dir.name;
            const name = dir.name || '';

            // Проверяем, является ли направление специализацией/авторской программой
            const isSpecialization =
                isSpecializationDirection(name, orgCode) ||
                isSpecializationDirection(displayName, orgCode) ||
                displayName === specializationTerm ||
                name === specializationTerm ||
                displayName.startsWith(specializationTerm + ':') ||
                name.startsWith(specializationTerm + ':');

            if (isSpecialization) {
                specializationDirs.push(dir);
            } else {
                otherDirs.push(dir);
            }
        });

        // Возвращаем: сначала специализации, потом остальные
        return [...specializationDirs, ...otherDirs];
    }, [directions, config]);

    if (isLoading) {
        return (
            <div className="loading">
                <div className="spinner"></div>
            </div>
        );
    }

    if (!sortedDirections || sortedDirections.length === 0) {
        return (
            <div className="directions-list-page">
                <div className="directions-list-container">
                    <button type="button" className="directions-list-back-link" onClick={handleBack}>
                        <ArrowLeft size={20} />
                        Назад
                    </button>
                    <h1>{directionTerms.many}</h1>
                    <p>На данный момент нет {directionTerms.manyGen}</p>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="directions-list-page">
            <div className="directions-list-container">
                <button type="button" className="directions-list-back-link" onClick={handleBack}>
                    <ArrowLeft size={20} />
                    Назад
                </button>
                <div className="directions-list-header">
                    <h1>
                        <BookOpen size={32} />
                        {directionTerms.many}
                    </h1>
                    <p>Выберите {directionTerms.oneLower}, чтобы узнать подробности и увидеть доступные группы</p>
                </div>

                <div className="directions-list">
                    {sortedDirections.map(direction => (
                        <Link
                            key={direction.id}
                            to={`/directions/${direction.id}`}
                            className="direction-row"
                        >
                            <div className="direction-row-content">
                                <h2 className="direction-row-title">
                                    {direction.full_name || direction.name}
                                </h2>
                            </div>

                            <div className="direction-row-arrow">
                                <ArrowRight size={20} />
                            </div>
                        </Link>
                    ))}
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default DirectionsListPage;

